import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { SubtaskModel, TaskStatus } from "../../Models/Models";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { deleteData } from "../../Services/deleteData";
import { urlEnum } from "../../Utils/urlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";

/**
 * Returns the base URL for subtasks related to a specific task.
 * @param taskId The ID of the task for which to get subtasks.
 * @returns The base URL for the subtasks.
 */
const subtaskBase = (taskId: number) => `${urlEnum.tasks}/${taskId}/subtasks`;

type Props = {
  taskId: number;
  initialSubtasks: SubtaskModel[];
};

/**
 * Renders a circular checkmark icon that can be toggled.
 * @param done A boolean indicating whether the checkmark is completed.
 * @param onChange A callback function to be called when the checkmark is toggled.
 * @returns  A JSX element representing the circular checkmark.
 */
function CircleCheck({
  done,
  onChange,
}: {
  done: boolean;
  onChange: () => void;
}) {
  return (
    <Box
      onClick={onChange}
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: done ? "none" : "2px solid #d1d5db",
        bgcolor: done ? "#FF6633" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        cursor: "pointer",
        transition: "all 0.15s",
        "&:hover": {
          borderColor: done ? undefined : "#FF6633",
          bgcolor: done ? "#e55520" : "#fff5f2",
        },
      }}
    >
      {done && <CheckIcon sx={{ fontSize: 11, color: "#fff" }} />}
    </Box>
  );
}

/**
 * Renders a list of subtasks for a given task.
 * @param param0 The props for the component, including the task ID and initial subtasks.
 * @returns A JSX element representing the subtask list.
 */
export default function SubtaskList({ taskId, initialSubtasks }: Props) {
  const [subtasks, setSubtasks] = useState<SubtaskModel[]>(initialSubtasks);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Effect hook to update the subtasks state when the initialSubtasks prop changes.
   */
  useEffect(() => {
    setSubtasks(initialSubtasks);
  }, [initialSubtasks]);

  /**
   * Effect hook to focus the input field when adding a new subtask.
   */
  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  /**
   * Calculates the number of completed subtasks.
   * @returns The count of completed subtasks.
   */
  const completed = subtasks.filter((s) => s.status === "completed").length;

  /**
   * Handles the addition of a new subtask.
   * Trims the new subtask name and posts it to the server if it's not empty.
   * Updates the subtasks state with the newly added subtask once the request resolves.
   * Resets the input field and adding state after processing.
   */
  function handleAdd() {
    const name = newName.trim();
    if (!name) {
      setAdding(false);
      return;
    }
    const subtask: SubtaskModel = {
      task_id: taskId,
      name,
      status: "not_started",
    };
    postData(subtaskBase(taskId), subtask).then((res: any) => {
      setSubtasks((prev) => [
        ...prev,
        res?.data?.subtask ?? res?.data ?? subtask,
      ]);
    });
    setNewName("");
    setAdding(false);
  }

  /**
   * Toggles the completion status of a subtask.
   * @param subtask
   */
  function handleToggle(subtask: SubtaskModel) {
    const newStatus: TaskStatus =
      subtask.status === "completed" ? "not_started" : "completed";
    const updated = { ...subtask, status: newStatus };
    setSubtasks((prev) => prev.map((s) => (s.id === subtask.id ? updated : s)));
    updateData(`${subtaskBase(taskId)}/${subtask.id}`, {
      status: newStatus,
    }).catch(() =>
      setSubtasks((prev) =>
        prev.map((s) => (s.id === subtask.id ? subtask : s)),
      ),
    );
  }

  /**
   *  Handles the deletion of a subtask.
   * @param subtask The subtask to delete.
   */
  function handleDelete(subtask: SubtaskModel) {
    setSubtasks((prev) => prev.filter((s) => s.id !== subtask.id));
    deleteData(`${subtaskBase(taskId)}/${subtask.id}`).catch(() =>
      setSubtasks((prev) => [...prev, subtask]),
    );
  }

  /**
   * Renders the subtask list component, including the header, subtask rows, and input for adding new subtasks.
   * @returns A JSX element representing the subtask list.
   */
  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              color: "#6b7280",
              letterSpacing: 0,
            }}
          >
            {Vocabulary.taskSubtasks}
          </Typography>
          {subtasks.length > 0 && (
            <Typography
              sx={{
                fontSize: 11,
                color: completed === subtasks.length ? "#16a34a" : "#9ca3af",
                fontWeight: 500,
              }}
            >
              {completed}/{subtasks.length}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => setAdding(true)}
          sx={{
            p: 0.25,
            color: "#9ca3af",
            "&:hover": { color: "#FF6633", bgcolor: "transparent" },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Rows */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {subtasks.map((subtask) => (
          <Box
            key={subtask.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              py: 0.6,
              px: 0.5,
              borderRadius: "6px",
              "&:hover": { bgcolor: "#f9fafb" },
              "&:hover .del-btn": { opacity: 1 },
            }}
          >
            <CircleCheck
              done={subtask.status === "completed"}
              onChange={() => handleToggle(subtask)}
            />
            <Typography
              sx={{
                flex: 1,
                fontSize: 13,
                lineHeight: 1.5,
                color: subtask.status === "completed" ? "#9ca3af" : "#374151",
                textDecoration:
                  subtask.status === "completed" ? "line-through" : "none",
              }}
            >
              {subtask.name}
            </Typography>
            <IconButton
              size="small"
              className="del-btn"
              onClick={() => handleDelete(subtask)}
              sx={{
                p: 0.25,
                opacity: 0,
                transition: "opacity 0.12s",
                color: "#d1d5db",
                "&:hover": { color: "#ef4444" },
              }}
            >
              <DeleteIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        ))}

        {/* Add input */}
        {adding && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              py: 0.6,
              px: 0.5,
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                border: "2px dashed #d1d5db",
                flexShrink: 0,
              }}
            />
            <input
              ref={inputRef}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
                if (e.key === "Escape") {
                  setAdding(false);
                  setNewName("");
                }
              }}
              onBlur={handleAdd}
              placeholder={`${Vocabulary.taskSubtaskNew}…`}
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                fontSize: 13,
                fontFamily: "inherit",
                color: "#374151",
                background: "transparent",
                padding: 0,
              }}
            />
          </Box>
        )}

        {/* Empty + add */}
        {!adding && subtasks.length === 0 && (
          <Box
            onClick={() => setAdding(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              py: 0.6,
              px: 0.5,
              cursor: "pointer",
              borderRadius: "6px",
              "&:hover .add-circle": { borderColor: "#FF6633" },
              "&:hover .add-text": { color: "#FF6633" },
            }}
          >
            <Box
              className="add-circle"
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                border: "2px dashed #d1d5db",
                transition: "border-color 0.12s",
                flexShrink: 0,
              }}
            />
            <Typography
              className="add-text"
              sx={{ fontSize: 13, color: "#9ca3af", transition: "color 0.12s" }}
            >
              {Vocabulary.taskSubtaskNew}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
